import { lazy, FC } from 'react';
import { View, DictionaryType } from '../../types';

const Dashboard = lazy(() => import('../dashboard/Dashboard'));
const WaybillsLayout = lazy(() => import('../waybills/WaybillsLayout'));
const Reports = lazy(() => import('../reports/Reports'));
const Admin = lazy(() => import('../admin/Admin'));
const Dictionaries = lazy(() => import('../dictionaries/Dictionaries'));
const GarageManagement = lazy(() => import('../dictionaries/GarageManagement'));
const OwnerPanel = lazy(() => import('../admin/OwnerPanel'));
const About = lazy(() => import('../about/About'));
const UserGuide = lazy(() => import('../help/UserGuide'));
const AdminGuide = lazy(() => import('../help/AdminGuide'));
const DeveloperGuide = lazy(() => import('../help/DeveloperGuide'));
const TestingGuide = lazy(() => import('../help/TestingGuide'));

interface AppRouterProps {
    currentView: View;
    waybillToOpen: string | null;
    onWaybillOpened: () => void;
    subView: string | null;
    dictionarySubView: DictionaryType | null;
    setDictionarySubView: (v: DictionaryType | null) => void;
    handleNavigate: (view: View, subView?: string) => void;
    handleNavigateToWaybill: (id: string) => void;
}

export const AppRouter: FC<AppRouterProps> = ({
    currentView,
    waybillToOpen,
    onWaybillOpened,
    subView,
    dictionarySubView,
    setDictionarySubView,
    handleNavigate,
    handleNavigateToWaybill,
}) => {
    switch (currentView) {
        case 'DASHBOARD':
            return <Dashboard onNavigateToWaybill={handleNavigateToWaybill} />;
        case 'WAYBILLS':
            return (
                <WaybillsLayout
                    waybillToOpen={waybillToOpen}
                    onWaybillOpened={onWaybillOpened}
                    subViewToOpen={subView}
                />
            );
        case 'DICTIONARIES':
            const subViewToOpen = dictionarySubView;
            // Note: We don't reset state here inside render, logic should be handled by parent or effect if strictly needed on mount.
            // But preserving original behavior:
            // In original App.tsx: if (dictionarySubView) setDictionarySubView(null);
            // This is a side effect in render! Not ideal, but we'll adapt.
            // Better to pass `onMount` prop to Dictionaries?
            // For now, let's keep it clean and maybe move the reset logic to the `handleNavigate` in parent or useEffect in Parent.
            return <Dictionaries subViewToOpen={subViewToOpen} />;
        case 'WAREHOUSE':
            return <GarageManagement />;
        case 'REPORTS':
            return <Reports />;
        case 'ADMIN':
            return <Admin />;
        case 'ABOUT':
            return <About />;
        case 'USER_GUIDE':
            return <UserGuide onNavigate={handleNavigate} />;
        case 'ADMIN_GUIDE':
            return <AdminGuide />;
        case 'DEVELOPER_GUIDE':
            return <DeveloperGuide />;
        case 'TESTING_GUIDE':
            return <TestingGuide />;
        case 'OWNER_PANEL':
            return <OwnerPanel />;
        default:
            return <Dashboard onNavigateToWaybill={handleNavigateToWaybill} />;
    }
};
